import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Select from "../../inputs/select";
import Radio from "../../inputs/radio";
import { InputTextarea } from "primereact/inputtextarea";
import CriterionSummary from "./CriterionSummary";
import { getLocation } from "../../../services/cohort";
import * as CONST from "../../../utilities/constants";
import "./LocationCard.scss";

/**
 * LocationCard - Criteria card for location targeting
 *
 * Data format: {
 *   countries: [{ id: "...", country_name: "..." }],
 *   regions: [{ id: "...", region_name: "...", country_code: "..." }],
 *   metros: [{ id: "...", metro_name: "...", country_code: "..." }],
 *   cities: [{ id: "...", city_name: "...", region_name: "...", country_code: "..." }],
 *   postalCodes: ["10010", "10011"],
 *   action: "include" | "exclude"
 * }
 */
function LocationCard({ data, onChange, isReadOnly, isCollapsed, cardId }) {
	const [locationOptions, setLocationOptions] = useState([]);
	const [loading, setLoading] = useState(true);

	// Fetch location options on mount
	useEffect(() => {
		const fetchOptions = async () => {
			try {
				setLoading(true);
				const response = await getLocation();
				const options = [];

				response.data.countries.forEach((el) => {
					options.push({
						label: `${el.country_name} (Country)`,
						value: `${el.id}||${el.country_name}||country`,
					});
				});
				response.data.regions.forEach((el) => {
					options.push({
						label: `${el.region_name}, ${el.country_code} (State/Region)`,
						value: `${el.id}||${el.region_name}||region||${el.country_code}`,
					});
				});
				response.data.metros.forEach((el) => {
					options.push({
						label: `${el.metro_name}, ${el.country_code} (DMA)`,
						value: `${el.id}||${el.metro_name}||metro||${el.country_code}`,
					});
				});
				response.data.cities.forEach((el) => {
					options.push({
						label: `${el.city_name}, ${el.region_name}, ${el.country_code} (City)`,
						value: `${el.id}||${el.city_name}||city||${el.region_name}||${el.country_code}`,
					});
				});

				setLocationOptions(options);
			} catch (error) {
				console.error("Failed to fetch location options:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchOptions();
	}, []);

	// Convert data to select format
	const getSelectValue = () => {
		const selected = [];

		if (data?.countries) {
			data.countries.forEach((c) => {
				const name = c.country_name || c.id;
				selected.push({
					label: `${name} (Country)`,
					value: `${c.id}||${name}||country`,
				});
			});
		}
		if (data?.regions) {
			data.regions.forEach((r) => {
				const name = r.region_name || r.id;
				const countryCode = r.country_code || "";
				selected.push({
					label: countryCode ? `${name}, ${countryCode} (State/Region)` : `${name} (State/Region)`,
					value: `${r.id}||${name}||region||${countryCode}`,
				});
			});
		}
		if (data?.metros) {
			data.metros.forEach((m) => {
				const name = m.metro_name || m.id;
				const countryCode = m.country_code || "";
				selected.push({
					label: countryCode ? `${name}, ${countryCode} (DMA)` : `${name} (DMA)`,
					value: `${m.id}||${name}||metro||${countryCode}`,
				});
			});
		}
		if (data?.cities) {
			data.cities.forEach((c) => {
				const name = c.city_name || c.id;
				const regionName = c.region_name || "";
				const countryCode = c.country_code || "";
				const labelParts = [name];
				if (regionName) labelParts.push(regionName);
				if (countryCode) labelParts.push(countryCode);
				selected.push({
					label: `${labelParts.join(", ")} (City)`,
					value: `${c.id}||${name}||city||${regionName}||${countryCode}`,
				});
			});
		}

		return selected;
	};

	// Handle location selection change
	const handleSelectChange = (selectedOptions) => {
		const countries = [];
		const regions = [];
		const metros = [];
		const cities = [];

		(selectedOptions || []).forEach((opt) => {
			const parts = opt.value.split("||");
			const id = parts[0];
			const name = parts[1];
			const type = parts[2];

			if (type === "country") {
				countries.push({ id, country_name: name });
			} else if (type === "region") {
				regions.push({ id, region_name: name, country_code: parts[3] });
			} else if (type === "metro") {
				metros.push({ id, metro_name: name, country_code: parts[3] });
			} else if (type === "city") {
				cities.push({ id, city_name: name, region_name: parts[3], country_code: parts[4] });
			}
		});

		onChange({
			...data,
			countries,
			regions,
			metros,
			cities,
		});
	};

	// Handle postal codes change
	const handlePostalCodesChange = (e) => {
		const postalCodes = e.target.value.split("\n");
		onChange({
			...data,
			postalCodes,
		});
	};

	// Handle action change
	const handleActionChange = (action) => {
		onChange({
			...data,
			action: action.value,
		});
	};

	// Get current action for Radio
	const getCurrentAction = () => {
		return CONST.ruleActions.find((a) => a.value === data?.action) || CONST.ruleActions[0];
	};

	// Collapsed view - show selected items as chips
	if (isCollapsed) {
		return (
			<div className="location-card collapsed">
				<CriterionSummary type="location" data={data} />
			</div>
		);
	}

	// Expanded view
	return (
		<div className="location-card expanded">
			{/* Include/Exclude action */}
			<div className="rule-action rule-action-location">
				<Radio
					options={CONST.ruleActions}
					name={`location-action-${cardId}`}
					style="list"
					disabled={isReadOnly}
					required={false}
					selected={getCurrentAction()}
					onInput={handleActionChange}
				/>
			</div>

			{/* Location selector */}
			<Select
				label="Add countries, regions, cities, and DMAs"
				name="location"
				multi={true}
				selected={getSelectValue()}
				options={locationOptions}
				required={false}
				disabled={isReadOnly || loading}
				onSubmit={handleSelectChange}
			/>

			{/* Postal codes textarea */}
			<div className="inputtextarea input-wrapper">
				<label>Add Postal Codes</label>
				<div className="chips-info p5">
					One postal code per line. For the U.S., enter 5-digit zip codes. For other countries, follow
					your country&apos;s postal code format.
				</div>
				<InputTextarea
					value={(data?.postalCodes || []).join("\n")}
					onChange={handlePostalCodesChange}
					rows={5}
					placeholder={"10010\n10011\n10012"}
					disabled={isReadOnly}
				/>
			</div>
		</div>
	);
}

LocationCard.propTypes = {
	data: PropTypes.shape({
		countries: PropTypes.array,
		regions: PropTypes.array,
		metros: PropTypes.array,
		cities: PropTypes.array,
		postalCodes: PropTypes.array,
		action: PropTypes.string,
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	isCollapsed: PropTypes.bool,
	cardId: PropTypes.string,
};

LocationCard.defaultProps = {
	data: { action: "include" },
	isReadOnly: false,
	isCollapsed: false,
	cardId: "0",
};

// Export helper for getting selected count (used in header)
LocationCard.getSelectedCount = (data) => {
	if (!data) return 0;
	let count = 0;
	count += data.countries?.length || 0;
	count += data.regions?.length || 0;
	count += data.metros?.length || 0;
	count += data.cities?.length || 0;
	count += data.postalCodes?.filter((p) => p.trim()).length || 0;
	return count;
};

export default LocationCard;
